import * as React from "react";
import {
  Button,
  Box,
  Typography,
  Snackbar,
  Link,
  Paper,
  CircularProgress,
} from "@mui/material";

import { useForm } from "react-hook-form";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";

import { FormFieldTextField } from "../shared/components/FormFieldTextField";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { resetPassword, type ResetPasswordOutput } from "aws-amplify/auth";

interface ForgotPasswordValues {
  email: string;
}

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control, handleSubmit, watch, formState } =
    useForm<ForgotPasswordValues>({
      mode: "all",
      defaultValues: {
        email: "",
      },
    });
  const watchedEmail = watch("email");

  const [message, setMessage] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSendResetEmail = async (data: ForgotPasswordValues) => {
    if (!data.email) {
      setMessage(t("auth.forgotPassword.emailRequired"));
      return;
    }

    setLoading(true);
    try {
      const output = await resetPassword({ username: data.email });
      handleResetPasswordNextSteps(output);
    } catch (error) {
      console.error("Error during password reset:", error);
      // Always show success message for security (don't reveal if email exists)
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  function handleResetPasswordNextSteps(output: ResetPasswordOutput) {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case "CONFIRM_RESET_PASSWORD_WITH_CODE": {
        setEmailSent(true);
        break;
      }
      case "DONE":
        setEmailSent(true);
        break;
    }
  }

  if (emailSent) {
    return (
      <Paper elevation={1} sx={{ maxWidth: "80%", p: { xs: 3, sm: 4, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {t("auth.forgotPassword.checkEmailTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            <Trans
              i18nKey="auth.forgotPassword.sentMessage"
              values={{ email: watchedEmail }}
              components={{
                bold: <Typography component="span" sx={{ color: "primary.main", fontWeight: 600 }} />,
              }}
            />
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {t("auth.forgotPassword.checkSpam")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
            fullWidth
          >
            {t("common.backToLogin")}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ maxWidth: "80%", p: { xs: 3, sm: 4, lg: 5 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {t("auth.forgotPassword.title")}
        </Typography>
        <Typography component="div" sx={{ color: "text.secondary", mt: 1 }}>
          {t("auth.forgotPassword.subtitle")}
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(handleSendResetEmail)}
          noValidate
          sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <FormFieldTextField
            control={control}
            required
            fullWidth
            id="email"
            label={t("common.emailLabel")}
            name="email"
            rules={{
              required: t("auth.forgotPassword.emailRequiredValidation"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("common.invalidEmail"),
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!formState.isValid || loading}
            sx={{
              mt: 2,
              mb: 2,
              fontSize: "1rem",
              backgroundColor: "primary.main",
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("auth.forgotPassword.submit")
            )}
          </Button>

          <Box sx={{ textAlign: "center" }}>
            <Link
              sx={{ textDecoration: "none", color: "primary.main" }}
              variant="body2"
              component={RouterLink}
              to="/login"
            >
              {t("common.backToLogin")}
            </Link>
          </Box>
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={message !== null}
          autoHideDuration={6000}
          message={message}
          onClose={() => setMessage(null)}
          ContentProps={{
            sx: {
              borderRadius: "5px",
              color: "white",
              bgcolor: "error.main",
              fontWeight: "bold",
              fontSize: "1rem",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ForgotPassword;
